/**
 * Online store, session B2 — an order a buyer placed on the shop's website
 * (migrations 151, 152), as the till sees it.
 *
 * The walk is new → confirmed → shipped, and any open order ends either
 * delivered — ONLY by a sale whose client_ref is `online:<order id>`, which
 * create_sale checks against the locked order line by line (152) — or
 * cancelled with a reason. The sale is exactly the order: its products, counts
 * and prices (the price the buyer saw, even if the shelf price changed since),
 * plus one no-product „მიტანა" line for the delivery fee. No discount, bonus,
 * promotion or wholesale price — and never an offline sale.
 *
 * The buyer's data is read by the owner and cashiers only (151's RLS).
 */

import type { Key } from './i18n'
import type { SaleItem } from './mock-data'

export type OnlineOrderStatus = 'new' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled'
export type OnlineMove = 'confirmed' | 'shipped' | 'sell' | 'cancelled'

export type OnlineOrderItem = {
  /** null when the product was deleted after the order (the line survives). */
  product_id: string | null
  name: string
  size: string | null
  color: string | null
  photo: string | null
  unit_price: number
  quantity: number
}

export type OnlineOrderEvent = {
  status: OnlineOrderStatus
  reason: string | null
  by_user: string | null
  at: string
}

export type OnlineOrder = {
  id: string
  number: number
  status: OnlineOrderStatus
  /** null after a retention purge (pii_purged_at) */
  customer_name: string | null
  customer_phone: string | null
  address: string | null
  city: string | null
  note: string | null
  delivery: 'courier' | 'pickup'
  payment_requested: 'on_delivery' | 'transfer' | 'at_shop'
  items_total: number
  delivery_fee: number
  total: number
  fulfillment_branch_id: string | null
  sale_id: string | null
  cancel_reason: string | null
  created_at: string
  status_at: string
  items: OnlineOrderItem[]
  events: OnlineOrderEvent[]
}

/** The columns the till reads (151); the lines and the moves come along. */
export const ONLINE_ORDER_SELECT =
  'id, number, status, customer_name, customer_phone, address, city, note, delivery, payment_requested, ' +
  'items_total, delivery_fee, total, fulfillment_branch_id, sale_id, cancel_reason, created_at, status_at, ' +
  'online_order_items(product_id, name, size, color, photo, unit_price, quantity), ' +
  'online_order_events(status, reason, by_user, at)'

const money = (v: unknown) => Math.round(Number(v ?? 0) * 100) / 100

export function mapOnlineOrder(r: any): OnlineOrder {
  return {
    id: r.id,
    number: Number(r.number ?? 0),
    status: r.status,
    customer_name: r.customer_name ?? null,
    customer_phone: r.customer_phone ?? null,
    address: r.address ?? null,
    city: r.city ?? null,
    note: r.note ?? null,
    delivery: r.delivery,
    payment_requested: r.payment_requested,
    items_total: money(r.items_total),
    delivery_fee: money(r.delivery_fee),
    total: money(r.total),
    fulfillment_branch_id: r.fulfillment_branch_id ?? null,
    sale_id: r.sale_id ?? null,
    cancel_reason: r.cancel_reason ?? null,
    created_at: r.created_at,
    status_at: r.status_at ?? r.created_at,
    items: ((r.online_order_items ?? []) as any[])
      .map((i) => ({
        product_id: i.product_id ?? null,
        name: i.name,
        size: i.size ?? null,
        color: i.color ?? null,
        photo: i.photo ?? null,
        unit_price: money(i.unit_price),
        quantity: Number(i.quantity),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    events: ((r.online_order_events ?? []) as any[])
      .map((e) => ({ status: e.status, reason: e.reason ?? null, by_user: e.by_user ?? null, at: e.at }))
      .sort((a, b) => a.at.localeCompare(b.at)),
  }
}

export const isOpenOrder = (s: OnlineOrderStatus) => s === 'new' || s === 'confirmed' || s === 'shipped'

/** What the shop may do next, in the order the buttons show. */
export function movesFor(s: OnlineOrderStatus): OnlineMove[] {
  if (s === 'new') return ['confirmed', 'shipped', 'sell', 'cancelled']
  if (s === 'confirmed') return ['shipped', 'sell', 'cancelled']
  if (s === 'shipped') return ['sell', 'cancelled']
  return []
}

export const orderNo = (n: number) => `#${n}`

/** The fee's line on the sale and the receipt — a document word, Georgian on any screen. */
export const DELIVERY_LINE = 'მიტანა'

/** The sale's client_ref — create_sale recognises it and delivers the order. */
export const onlineRef = (id: string) => `online:${id}`

/**
 * The lines create_sale must receive for this order — nothing more, nothing
 * less: each line at the order's own price, then one no-product line for the
 * delivery fee when there is one. Null when a product was deleted since the
 * order (152 refuses it: ONLINE_ORDER_PRODUCT_GONE — the shop cancels).
 */
export function saleLinesFor(o: OnlineOrder, deliveryName: string): Omit<SaleItem, 'id' | 'sale_id' | 'unit_cost'>[] | null {
  if (o.items.some((i) => !i.product_id)) return null
  const lines: Omit<SaleItem, 'id' | 'sale_id' | 'unit_cost'>[] = o.items.map((i) => ({
    product_id: i.product_id as string,
    product_name: i.name,
    barcode: null,
    quantity: i.quantity,
    unit_price: i.unit_price,
    total_price: money(i.unit_price * i.quantity),
  }))
  if (o.delivery_fee > 0) {
    // create_sale reads '' as „no product" (the repair line does the same, 130)
    lines.push({ product_id: '', product_name: deliveryName, barcode: null, quantity: 1, unit_price: o.delivery_fee, total_price: o.delivery_fee })
  }
  return lines
}

/**
 * The open order's lines the shelf cannot fill right now (the till is never
 * blocked by a web order, so the last unit may have been sold at the counter).
 * `stockOf` answers for this device's branch lens. A deleted product is short.
 */
export function shortLines(o: OnlineOrder, stockOf: (productId: string) => number): (string | null)[] {
  if (!isOpenOrder(o.status)) return []
  return o.items.filter((i) => !i.product_id || stockOf(i.product_id) < i.quantity).map((i) => i.product_id)
}

const digits = (s: string) => s.replace(/\D/g, '')

/** A number („12", „#12"), a name, or a phone in any spacing. */
export function orderMatches(o: OnlineOrder, q: string): boolean {
  const s = q.trim().toLowerCase()
  if (!s) return true
  if (s.replace(/^#/, '') === String(o.number)) return true
  if ((o.customer_name ?? '').toLowerCase().includes(s)) return true
  const d = digits(s)
  return d.length >= 3 && digits(o.customer_phone ?? '').includes(d)
}

/** The till's words for create_sale's and set_online_order_status's refusals. */
export function onlineErrorKey(message: string): Key | null {
  const m = /ONLINE_ORDER_([A-Z_]+)/.exec(message)
  if (!m) return null
  const map: Record<string, Key> = {
    MISMATCH: 'oo.err.mismatch',
    CLOSED: 'oo.err.closed',
    BRANCH: 'oo.err.branch',
    MOVE: 'oo.err.move',
    PRODUCT_GONE: 'oo.err.gone',
    NOT_FOUND: 'oo.err.notFound',
    TERMS: 'oo.err.terms',
    REASON: 'oo.err.reason',
    // 152 after Codex's review: the database refuses a transfer order until the owner picks its rail
    TRANSFER: 'oo.sell.transfer',
  }
  return map[m[1]] ?? null
}
